import { useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { ACCENT as SECTION_COLOR, OK } from '../../lib/uiTokens';

/**
 * Pokyny pro AI čtení dokladů od jednoho dodavatele.
 *
 * Je to **nastavení dodavatele**, ne údaj dokladu — projeví se až u příštího
 * skenu (nebo po „Rescan AI"). Proto se neválí v panelu zaúčtování jako
 * rozbalovací řádek, ale sedí za ikonou u jména dodavatele: kdo je nepotřebuje,
 * o nich neví, a kdo ano, najde je tam, kde je ten dodavatel.
 */
export default function PokynyAiModal({ dodavatel, isDark = false, onClose, onUlozeno }) {
    const [text, setText] = useState(dodavatel?.scan_pokyny || '');
    const [jenSouhrn, setJenSouhrn] = useState(!!dodavatel?.scan_jen_souhrn);
    const [uklada, setUklada] = useState(false);

    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';

    const uloz = async () => {
        setUklada(true);
        try {
            const r = await fetch(`/api/dodavatele/${dodavatel.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json', 'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                },
                body: JSON.stringify({ nazev: dodavatel.nazev, scan_pokyny: text, scan_jen_souhrn: jenSouhrn }),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.message || 'Uložení pokynů selhalo'); return; }
            onUlozeno?.();
            onClose?.();
        } catch { alert('Síťová chyba'); }
        finally { setUklada(false); }
    };

    return (
        <ModalShell open onClose={onClose} isDark={isDark} sirka={440}
            nadpis="Pokyny pro AI čtení" ikona={Sparkles} barva={SECTION_COLOR}
            paticka={(
                <div className="flex items-center gap-2">
                    <span className="text-[11px] flex-1" style={{ color: 'var(--text-tertiary)' }}>
                        Projeví se při dalším skenu tohoto dodavatele nebo po „Rescan AI".
                    </span>
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 rounded-lg text-xs"
                        style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}`, background: 'transparent' }}>
                        Zavřít
                    </button>
                    <button type="button" onClick={uloz} disabled={uklada}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ background: SECTION_COLOR, opacity: uklada ? 0.6 : 1 }}>
                        {uklada ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Uložit
                    </button>
                </div>
            )}>
            <div className="text-[12px] mb-2" style={{ color: 'var(--text-secondary)' }}>
                Co má AI u dokladů od <b style={{ color: 'var(--text-primary)' }}>{dodavatel?.nazev}</b> hlídat.
                {dodavatel?.scan_pokyny && <span style={{ color: OK }}> · pokyny nastaveny</span>}
            </div>
            <label className="flex items-start gap-2 mb-3 px-2.5 py-2 rounded-lg cursor-pointer"
                style={{ border: `1px solid ${jenSouhrn ? `${SECTION_COLOR}66` : hranice}` }}>
                <input type="checkbox" checked={jenSouhrn} onChange={e => setJenSouhrn(e.target.checked)}
                    className="mt-0.5" style={{ accentColor: SECTION_COLOR }} />
                <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                    <b style={{ color: 'var(--text-primary)' }}>Brát jen souhrn (rekapitulaci DPH)</b><br />
                    Řádky faktury se nečtou — doklad dostane jeden řádek na každou sazbu DPH. Pro dlouhé faktury typu telefon, energie.
                </span>
            </label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={7} autoFocus
                placeholder="Např.: Zálohy sudů (KEG) jsou 0 % a nepatří do DPH rozpadu. Když je celková částka ručně upravená o vrácené obaly, ber tu ručně dopsanou částku k úhradě."
                className="w-full rounded-lg px-2.5 py-2 text-[13px]"
                style={{
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)',
                    border: `1px solid ${hranice}`, color: 'var(--text-primary)', outline: 'none', resize: 'vertical',
                }} />
        </ModalShell>
    );
}
